import { Fragment, type FunctionComponent, type ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconEdit } from '~/ui/core/icons';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';

import type { LearningOpportunity } from '../data-contract';
import { TakeCourseButton } from './TakeCourseButton';

export const CourseActionBar: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <div
        className="flex items-center justify-between gap-l rounded-xl bg-surface-hover p-xxs shadow-sm"
        data-testid="learning-opportunity-action-bar"
    >
        <div />
        <div className="flex items-center gap-s">{children}</div>
    </div>
);

export const CourseActionBarContent: FunctionComponent<{
    readonly canUserEditLearningOpportunity: boolean;
    readonly isEditing: boolean;
    readonly isPermissionsPending: boolean;
    readonly isSubmitting: boolean;
    readonly learningOpportunity?: LearningOpportunity;
    readonly onCancel: () => void;
    readonly onEdit?: () => void;
}> = ({
    canUserEditLearningOpportunity,
    isEditing,
    isPermissionsPending,
    isSubmitting,
    learningOpportunity,
    onCancel,
    onEdit,
}) => {
    const translate = useTranslate();

    if (isEditing) {
        return (
            <Fragment>
                <Button disabled={isSubmitting} type="button" variant="tertiary" onClick={onCancel}>
                    {translate('dialogs.buttons.cancel')}
                </Button>
                <Button disabled={isSubmitting} type="submit" variant="primary">
                    {translate('common.save')}
                </Button>
            </Fragment>
        );
    }
    if (learningOpportunity === undefined || isPermissionsPending) {
        return <Skeleton height="32px" width="100px" />;
    }
    if (canUserEditLearningOpportunity && onEdit !== undefined) {
        return (
            <Button iconPosition="leading" type="button" variant="tertiary" onClick={onEdit}>
                <IconEdit size={14} />
                {translate('common.edit')}
            </Button>
        );
    }

    return <TakeCourseButton learningOpportunity={learningOpportunity} />;
};
